import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { supportedLanguages, translations, getLanguageConfig } from '../i18n/translations';
import { getBuildTimeBranding } from '../config/buildTimeConfig';

const LocalizationContext = createContext();
const STORAGE_KEY = 'app_language';
const FALLBACK_LANGUAGE = 'en';

const getNestedValue = (source, path) =>
  String(path || '')
    .split('.')
    .filter(Boolean)
    .reduce((acc, key) => (acc && Object.prototype.hasOwnProperty.call(acc, key) ? acc[key] : undefined), source);

const interpolate = (template, params) =>
  String(template).replace(/\{\{([^}]+)\}\}/g, (_match, key) => (params[key] ?? ''));

const getInitialLanguage = () => {
  try {
    const savedLanguage = localStorage.getItem(STORAGE_KEY);
    if (savedLanguage && translations[savedLanguage]) {
      return savedLanguage;
    }
  } catch (_e) {
    // Ignore storage read errors and use fallback.
  }

  const browserLanguage = window.navigator?.language?.toLowerCase() || '';
  
  // Find matching language based on browser prefixes
  const matchedLang = supportedLanguages.find(lang =>
    lang.browserPrefixes.some(prefix => browserLanguage.startsWith(prefix))
  );
  
  if (matchedLang) {
    return matchedLang.code;
  }

  return FALLBACK_LANGUAGE;
};

export const LocalizationProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);
  const tenantBrand = useMemo(() => getBuildTimeBranding(language), [language]);  const tenantBrandEnglish = useMemo(() => getBuildTimeBranding('en'), []);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch (_e) {
      // Ignore storage write errors.
    }
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => {
    const langConfig = getLanguageConfig(language);
    
    const t = (key, params = {}, fallback = '') => {
      const selectedText = getNestedValue(translations[language], key);
      const fallbackText = getNestedValue(translations[FALLBACK_LANGUAGE], key);
      const template = selectedText ?? fallbackText ?? (fallback || key);
      return typeof template === 'string'
        ? interpolate(template, {
            tenantName: tenantBrand.fullName,
            tenantFullName: tenantBrand.fullName,
            tenantShortName: tenantBrand.shortName,
            ...params,
          })
        : template;
    };

    return {
      language,
      setLanguage,
      locale: langConfig.locale,
      ogLocale: langConfig.ogLocale,
      isRtl: langConfig.rtl,
      needsCompactFont: langConfig.needsCompactFont,
      tenantBrand,
      tenantBrandEnglish,
      supportedLanguages,
      t,
    };
  }, [language, tenantBrand]);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within LocalizationProvider');
  }
  return context;
};
